import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, ArrowRight, AlertCircle, Check, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

const LOGO_LIGHT = "/images/logos/logo-color.png";
const LOGO_DARK = "/images/logos/logo-white.png";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const { theme } = useTheme();

  const token = searchParams.get('token') || '';
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tokenStatus, setTokenStatus] = useState('checking'); // checking | valid | missing | invalid | expired

  useEffect(() => {
    let cancelled = false;
    async function verify() {
      if (!token) {
        if (!cancelled) setTokenStatus('missing');
        return;
      }
      try {
        const res = await fetch(`${API_URL}/api/auth/verify-reset-token?token=${encodeURIComponent(token)}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.valid) setTokenStatus('valid');
        else setTokenStatus(data.reason || 'invalid');
      } catch {
        if (!cancelled) setTokenStatus('invalid');
      }
    }
    verify();
    return () => { cancelled = true; };
  }, [token, API_URL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    if (password !== confirm) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, password, confirm);
      setSuccess(true);
      setTimeout(() => navigate('/auth/login'), 2500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-card border border-border rounded-2xl p-8 shadow-lg">
            <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-6">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Mot de passe réinitialisé</h1>
            <p className="text-muted-foreground mb-6">
              Votre mot de passe a été mis à jour. Vous allez être redirigé vers la page de connexion...
            </p>
            <Link to="/auth/login">
              <Button className="btn-primary" data-testid="reset-success-login-btn">
                Se connecter maintenant
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (tokenStatus === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="reset-checking">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (tokenStatus !== 'valid') {
    const errorText = {
      missing: "Le lien de réinitialisation est incomplet. Vérifiez que vous avez bien copié l'URL complète depuis votre email.",
      invalid: "Ce lien n'est plus valide. Il a peut-être déjà été utilisé ou n'existe pas.",
      expired: "Ce lien de réinitialisation a expiré (durée de validité : 1 heure). Merci d'en demander un nouveau.",
    }[tokenStatus] || "Ce lien ne peut pas être utilisé.";

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md text-center">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-6">
              <img
                src={theme === 'dark' ? LOGO_DARK : LOGO_LIGHT}
                alt="Axeris Labs"
                className="h-12 w-auto object-contain mx-auto"
              />
            </Link>
          </div>
          <div className="bg-card border border-border rounded-2xl p-8 shadow-lg" data-testid={`reset-error-${tokenStatus}`}>
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Lien invalide</h1>
            <p className="text-muted-foreground mb-6">{errorText}</p>
            <div className="flex flex-col gap-3">
              <Link to="/auth/forgot-password">
                <Button className="btn-primary w-full" data-testid="reset-request-new-link">
                  Demander un nouveau lien
                </Button>
              </Link>
              <Link to="/auth/login" className="text-sm text-muted-foreground hover:text-foreground">
                Retour à la connexion
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <img
              src={theme === 'dark' ? LOGO_DARK : LOGO_LIGHT}
              alt="Axeris Labs"
              className="h-12 w-auto object-contain mx-auto"
            />
          </Link>
          <h1 className="text-3xl font-bold mb-2">Nouveau mot de passe</h1>
          <p className="text-muted-foreground">Choisissez un mot de passe sécurisé (8 caractères minimum)</p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-8 shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm" data-testid="reset-error">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Nouveau mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type={showPwd ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  required
                  minLength={8}
                  data-testid="reset-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                  aria-label="Afficher / masquer le mot de passe"
                >
                  {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Confirmer le mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type={showPwd ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="pl-10"
                  required
                  minLength={8}
                  data-testid="reset-password-confirm"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full btn-primary"
              disabled={loading || !token}
              data-testid="reset-submit"
            >
              {loading ? 'Enregistrement...' : 'Réinitialiser le mot de passe'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            <Link to="/auth/login" className="text-primary hover:underline font-medium">
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
